const MODULE_RELOAD_ATTEMPTS_KEY = "nitfisio:module-reload-attempts";
const LEGACY_MODULE_RELOAD_ATTEMPTED_KEY = "nitfisio:module-reload-attempted";
const MODULE_RELOAD_QUERY_PARAM = "module-reload-attempt";
const MAX_MODULE_RELOAD_ATTEMPTS = 2;
const MODULE_RECOVERY_RESET_DELAY_MS = 15_000;

/**
 * Only match explicit module-import failure messages.
 * Do NOT match generic chunk URLs — those appear in normal stack traces too.
 */
const MODULE_FAILURE_PATTERN =
  /importing a module script failed|failed to fetch dynamically imported module|error loading dynamically imported module/i;

/**
 * Symptoms of a broken vendor chunk split: a chunk evaluates before its
 * dependency (typically React) and crashes reading `forwardRef`, `createContext`,
 * `useState`, etc. from `undefined`. Treated as a recoverable load failure
 * because clearing the SW/caches usually restores a consistent build.
 */
const VENDOR_CHUNK_BROKEN_PATTERN =
  /Cannot read propert(?:y|ies) of undefined \(reading '(?:forwardRef|createContext|useState|useEffect|useRef|useContext|useMemo|useCallback|useLayoutEffect|Children|Fragment|jsx|jsxs|jsxDEV)'\)/i;

function readFailureText(value: unknown) {
  if (typeof value === "string") return value;
  if (value instanceof Error) return value.message || "";
  return "";
}

function readRecoveryAttempts() {
  if (typeof window === "undefined") return 0;

  let attemptsFromStorage = 0;
  try {
    attemptsFromStorage = Number.parseInt(sessionStorage.getItem(MODULE_RELOAD_ATTEMPTS_KEY) || "0", 10);
  } catch {
    attemptsFromStorage = 0;
  }

  try {
    const url = new URL(window.location.href);
    const attemptsFromQuery = Number.parseInt(url.searchParams.get(MODULE_RELOAD_QUERY_PARAM) || "0", 10);
    return Number.isFinite(attemptsFromQuery)
      ? Math.max(attemptsFromStorage, attemptsFromQuery)
      : attemptsFromStorage;
  } catch {
    return attemptsFromStorage;
  }
}

export function hasExceededModuleImportRecoveryAttempts() {
  return readRecoveryAttempts() >= MAX_MODULE_RELOAD_ATTEMPTS;
}

/**
 * Recovery is allowed in BOTH preview and production. The attempt counter
 * (capped at MAX_MODULE_RELOAD_ATTEMPTS) prevents infinite reload loops if
 * the failure is genuinely permanent (network down, deleted asset, etc.).
 */
export function canAttemptModuleImportRecovery() {
  if (typeof window === "undefined") return false;
  return !hasExceededModuleImportRecoveryAttempts();
}

export function isPreviewOrIframeContext() {
  if (typeof window === "undefined") return false;

  const host = window.location.hostname.toLowerCase();

  try {
    if (window.self !== window.top) return true;
  } catch {
    return true;
  }

  return host.includes("id-preview--") || host.includes("lovableproject.com");
}

/**
 * Detect real module-import failures.
 * Only matches the explicit error message — NOT generic chunk filenames.
 */
export function isModuleImportFailure(value: unknown) {
  if (typeof value !== "string" && !(value instanceof Error)) return false;

  const message = readFailureText(value);
  return MODULE_FAILURE_PATTERN.test(message) || VENDOR_CHUNK_BROKEN_PATTERN.test(message);
}

export function resetModuleImportRecoveryState() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(MODULE_RELOAD_ATTEMPTS_KEY);
    sessionStorage.removeItem(LEGACY_MODULE_RELOAD_ATTEMPTED_KEY);
  } catch {
    // Ignore
  }
}

/**
 * Schedule a delayed reset so the counter survives the initial page load
 * and only clears after the app has stabilised.
 */
export function scheduleModuleImportRecoveryStateReset() {
  if (typeof window === "undefined") return;
  window.setTimeout(() => {
    resetModuleImportRecoveryState();
  }, MODULE_RECOVERY_RESET_DELAY_MS);
}

async function clearModuleCaches() {
  if (typeof window === "undefined") return;
  try {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(
        registrations
          .filter((r) => {
            const url = r.active?.scriptURL ?? r.installing?.scriptURL ?? "";
            return !url.includes("sw-push");
          })
          .map((r) => r.unregister()),
      );
    }
    if ("caches" in window) {
      const cacheKeys = await caches.keys();
      await Promise.all(cacheKeys.map((k) => caches.delete(k)));
    }
  } catch {
    // Best effort
  }
}

const PREVIEW_CACHE_CLEANUP_KEY = "nitfisio:preview-cache-cleaned";
let globalModuleRecoveryInstalled = false;

/**
 * Only run the (expensive) cache+SW wipe ONCE per browser session.
 * Running it on every boot in the preview iframe was unnecessarily
 * delaying first paint and re-triggering stale-chunk loops.
 */
export async function cleanupPreviewModuleCaches() {
  if (!isPreviewOrIframeContext()) return;
  try {
    if (sessionStorage.getItem(PREVIEW_CACHE_CLEANUP_KEY) === "1") return;
    sessionStorage.setItem(PREVIEW_CACHE_CLEANUP_KEY, "1");
  } catch {
    // If sessionStorage is unavailable, fall through and clean anyway.
  }
  await clearModuleCaches();
}

export async function recoverFromModuleImportFailure() {
  if (typeof window === "undefined") return false;
  if (!canAttemptModuleImportRecovery()) return false;
  const attempts = readRecoveryAttempts();

  try {
    sessionStorage.setItem(MODULE_RELOAD_ATTEMPTS_KEY, String(attempts + 1));
  } catch {
    // Ignore
  }

  await clearModuleCaches();

  try {
    const url = new URL(window.location.href);
    url.searchParams.set("v", Date.now().toString());
    url.searchParams.set(MODULE_RELOAD_QUERY_PARAM, String(attempts + 1));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }

  return true;
}

function attemptGlobalModuleRecovery(value: unknown) {
  if (!isModuleImportFailure(value)) return false;
  if (!canAttemptModuleImportRecovery()) return false;
  void recoverFromModuleImportFailure();
  return true;
}

/**
 * Vite dynamic imports can fail before React renders the lazy component
 * (for example preload/prefetch chunks or components outside an error
 * boundary). Install a tiny global safety net so those failures use the same
 * bounded cache-clean/reload path instead of leaving a blank screen.
 */
export function installGlobalModuleLoadRecovery() {
  if (typeof window === "undefined" || globalModuleRecoveryInstalled) return;
  globalModuleRecoveryInstalled = true;

  window.addEventListener("vite:preloadError", (event) => {
    const preloadEvent = event as Event & { payload?: unknown; preventDefault: () => void };
    if (attemptGlobalModuleRecovery(preloadEvent.payload)) {
      preloadEvent.preventDefault();
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (attemptGlobalModuleRecovery(event.reason)) {
      event.preventDefault();
    }
  });

  window.addEventListener(
    "error",
    (event) => {
      if (attemptGlobalModuleRecovery(event.error ?? event.message)) {
        event.preventDefault();
      }
    },
    true,
  );
}
